import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type FabricSourceRow = {
  sourceId: string;
  name: string;
  provider: string;
  pack: string;
  category: string;
  country: string | null;
  sourceType: string;
  adapter: string;
  costClass: string;
  authType: string;
  reliability: number;
  documentationUrl: string | null;
  status: string;
  httpStatus: number | null;
  latency: number | null;
  items: number | null;
  lastCheckedAt: string | null;
  error: string | null;
};

export type FabricOverview = {
  registry: {
    total: number;
    enabled: number;
    keyless: number;
    authRequired: number;
    countries: number;
    byPack: { pack: string; count: number }[];
    byAdapter: { adapter: string; count: number }[];
    byType: { type: string; count: number }[];
  };
  validated: number;
  active: number;
  degraded: number;
  unavailable: number;
  rateLimited: number;
  authRequiredValidated: number;
  deprecated: number;
  sources: FabricSourceRow[];
};

/** Registry snapshot + persisted health, for the Source Explorer. */
export const getFabricOverview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async (): Promise<FabricOverview> => {
    const { SOURCES, registryStats, declaredStatus } = await import("@/lib/intel/fabric/registry");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const stats = registryStats();

    const { data } = await (supabaseAdmin as unknown as {
      from: (t: string) => { select: (c: string) => Promise<{ data: Record<string, unknown>[] | null }> };
    })
      .from("osint_sources")
      .select(
        "source_id, health_status, http_status, average_latency, last_items, last_error, last_checked_at",
      );
    const health = new Map((data ?? []).map((r) => [String(r["source_id"]), r]));

    const sources: FabricSourceRow[] = SOURCES.map((s) => {
      const h = health.get(s.id);
      return {
        sourceId: s.id,
        name: s.name,
        provider: s.provider,
        pack: s.pack,
        category: s.category,
        country: s.country ?? null,
        sourceType: s.sourceType,
        adapter: s.adapter,
        costClass: s.costClass,
        authType: s.authType,
        reliability: s.reliability,
        documentationUrl: s.documentationUrl ?? null,
        status: h ? String(h["health_status"]) : declaredStatus(s),
        httpStatus: h ? (h["http_status"] as number | null) : null,
        latency: h ? (h["average_latency"] as number | null) : null,
        items: h ? (h["last_items"] as number | null) : null,
        lastCheckedAt: h ? (h["last_checked_at"] as string | null) : null,
        error: h ? (h["last_error"] as string | null) : null,
      };
    });

    const countStatus = (s: string) => sources.filter((r) => r.status === s).length;
    return {
      registry: {
        total: stats.total,
        enabled: stats.enabled,
        keyless: stats.keyless,
        authRequired: stats.authRequired,
        countries: stats.countries,
        byPack: stats.byPack,
        byAdapter: stats.byAdapter,
        byType: stats.byType,
      },
      validated: health.size,
      active: countStatus("ACTIVE"),
      degraded: countStatus("DEGRADED"),
      unavailable: countStatus("TEMPORARILY_UNAVAILABLE"),
      rateLimited: countStatus("RATE_LIMITED"),
      authRequiredValidated: countStatus("AUTH_REQUIRED"),
      deprecated: countStatus("DEPRECATED"),
      sources,
    };
  });

/** Run live validation over a slice of the registry and persist health. */
export const validateFabricSources = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ offset: z.number().int().min(0).max(5000).default(0), limit: z.number().int().min(1).max(120).default(60) }).parse(d),
  )
  .handler(async ({ data }) => {
    const { validateBatch, persistValidation } = await import("@/lib/intel/fabric/validate.server");
    const results = await validateBatch(null, { offset: data.offset, limit: data.limit, concurrency: 14, budgetMs: 50_000 });
    const stored = await persistValidation(results);
    const tally = results.reduce<Record<string, number>>((acc, r) => {
      acc[r.status] = (acc[r.status] ?? 0) + 1;
      return acc;
    }, {});
    return { checked: results.length, stored, tally };
  });

/** Ad-hoc fabric query (Source Explorer "test this plan"). */
export const previewFabricPlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        target: z.string().min(2).max(120),
        entity: z.enum(["person", "organization", "company", "domain", "location", "keyword"]).default("person"),
        country: z.string().max(3).optional(),
        budget: z.enum(["QUICK", "STANDARD", "DEEP", "EXHAUSTIVE"]).default("QUICK"),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { routeSources, planQueries } = await import("@/lib/intel/fabric/router");
    const domain = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(data.target) ? data.target : undefined;
    const defs = routeSources({ target: data.target, entity: data.entity, country: data.country, domain, budget: data.budget });
    return {
      planned: defs.length,
      queries: planQueries({ target: data.target, entity: data.entity, domain }),
      categories: Object.entries(
        defs.reduce<Record<string, number>>((acc, d) => {
          acc[d.category] = (acc[d.category] ?? 0) + 1;
          return acc;
        }, {}),
      )
        .sort((a, b) => b[1] - a[1])
        .map(([category, count]) => ({ category, count })),
      sources: defs.slice(0, 40).map((d) => ({ id: d.id, name: d.name, provider: d.provider, pack: d.pack })),
    };
  });
